import React from "react";
import { DashboardLayout } from "../components/dashboard/DashboardLayout";
import { apiRequest, ApiError } from "../api/client";
import { useAuth } from "../contexts/AuthContext";

type OverviewResponse = {
  asOf: string;
  users: { total: number; new30d: number };
  plans: { freeActive: number; trialActive: number; proActive: number };
  activity: { dau: number; wau: number; mau: number; events24h: number };
  billing: { conversions24h: number };
};

type DailyRow = {
  kpi_date: string;
  users_total: number;
  free_active: number;
  trial_active: number;
  pro_active: number;
  dau: number;
  wau: number;
  mau: number;
  conversions_24h: number;
};

type DailyResponse = {
  days: number;
  rows: DailyRow[];
};

function formatDateTime(value: string) {
  try {
    return new Date(value).toLocaleString("es-ES");
  } catch {
    return value;
  }
}

export default function AdminMetricsPage() {
  const { token } = useAuth();
  const [overview, setOverview] = React.useState<OverviewResponse | null>(null);
  const [daily, setDaily] = React.useState<DailyRow[]>([]);
  const [days, setDays] = React.useState(30);
  const [loading, setLoading] = React.useState(true);
  const [snapshotLoading, setSnapshotLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const load = React.useCallback(
    async (windowDays: number) => {
      if (!token) return;
      setLoading(true);
      setError(null);
      try {
        const [overviewData, dailyData] = await Promise.all([
          apiRequest<OverviewResponse>("/admin/metrics/overview", { token }),
          apiRequest<DailyResponse>(`/admin/metrics/daily?days=${windowDays}`, { token }),
        ]);
        setOverview(overviewData);
        setDaily(dailyData.rows);
      } catch (err) {
        if (err instanceof ApiError && err.status === 403) {
          setError("Esta sección está disponible solo para usuarios administradores.");
        } else {
          setError("No se pudieron cargar las métricas de administración.");
        }
      } finally {
        setLoading(false);
      }
    },
    [token]
  );

  React.useEffect(() => {
    void load(days);
  }, [load, days]);

  const runSnapshot = async () => {
    if (!token) return;
    setSnapshotLoading(true);
    setError(null);
    try {
      await apiRequest("/admin/metrics/snapshot", { method: "POST", token });
      await load(days);
    } catch {
      setError("No se pudo generar el snapshot manual.");
    } finally {
      setSnapshotLoading(false);
    }
  };

  return (
    <DashboardLayout active="admin">
      <section className="analysis-page-header">
        <div className="analysis-page-header-inner">
          <div className="analysis-page-heading">
            <h1>Métricas de administración</h1>
            <p>Monitoriza uso de producto, planes Free/Pro y conversión.</p>
          </div>
          <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
            <label className="analysis-year-filter">
              <span>Ventana (días)</span>
              <select className="input" value={days} onChange={e => setDays(Number(e.target.value))}>
                <option value={7}>7</option>
                <option value={30}>30</option>
                <option value={90}>90</option>
              </select>
            </label>
            <button type="button" className="btn btn-primary" onClick={runSnapshot} disabled={snapshotLoading || loading}>
              {snapshotLoading ? "Actualizando..." : "Snapshot manual"}
            </button>
          </div>
        </div>
      </section>

      <div className="analysis-page-content">
        {error ? <div className="panel" style={{ borderColor: "#fca5a5", color: "#991b1b" }}>{error}</div> : null}
        {loading ? (
          <div className="panel">Cargando métricas...</div>
        ) : overview ? (
          <>
            <div className="cards-grid" style={{ marginTop: 0 }}>
              <article className="card">
                <strong>Usuarios totales</strong>
                <p className="stat-value">{overview.users.total}</p>
                <p className="text-muted">Nuevos 30d: {overview.users.new30d}</p>
              </article>
              <article className="card">
                <strong>Planes activos</strong>
                <p className="text-muted">Free: {overview.plans.freeActive}</p>
                <p className="text-muted">Trial: {overview.plans.trialActive}</p>
                <p className="text-muted">Pro: {overview.plans.proActive}</p>
              </article>
              <article className="card">
                <strong>Actividad</strong>
                <p className="text-muted">DAU: {overview.activity.dau}</p>
                <p className="text-muted">WAU: {overview.activity.wau}</p>
                <p className="text-muted">MAU: {overview.activity.mau}</p>
              </article>
              <article className="card">
                <strong>Conversión facturación</strong>
                <p className="stat-value">{overview.billing.conversions24h}</p>
                <p className="text-muted">Conversiones últimas 24h</p>
              </article>
            </div>

            <div className="panel" style={{ marginTop: "1rem" }}>
              <h3 className="panel-title">Serie diaria</h3>
              <p className="panel-subtitle">Última actualización: {formatDateTime(overview.asOf)}</p>
              <div style={{ overflowX: "auto", marginTop: "0.75rem" }}>
                <table className="results-table" style={{ minWidth: 920 }}>
                  <thead>
                    <tr>
                      <th>Fecha</th>
                      <th>Usuarios</th>
                      <th>Free</th>
                      <th>Trial</th>
                      <th>Pro</th>
                      <th>DAU</th>
                      <th>WAU</th>
                      <th>MAU</th>
                      <th>Conv. 24h</th>
                    </tr>
                  </thead>
                  <tbody>
                    {daily.length ? (
                      daily.map(row => (
                        <tr key={row.kpi_date}>
                          <td>{row.kpi_date}</td>
                          <td>{row.users_total}</td>
                          <td>{row.free_active}</td>
                          <td>{row.trial_active}</td>
                          <td>{row.pro_active}</td>
                          <td>{row.dau}</td>
                          <td>{row.wau}</td>
                          <td>{row.mau}</td>
                          <td>{row.conversions_24h}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={9}>Sin datos todavía para esa ventana.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        ) : null}
      </div>
    </DashboardLayout>
  );
}

